
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, doc, runTransaction, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { calculateVat, formatCurrency, RECURRENCE_OPTIONS, advanceDate } from '../utils/invoiceUtils';

export default function CreateInvoice({ userRole }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([{ description: '', quantity: 1, price: 0 }]);
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientAddress: '',
    clientPhone: '',
    dueDate: '',
    notes: '',
    companyName: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    vatRate: 0,
    taxInclusive: false,
    taxRegNo: '',
    recurrenceFrequency: 'none'
  });
  const [loading, setLoading] = useState(false);
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        let snapshot;
        if (userRole === 'admin') {
          snapshot = await getDocs(query(collection(db, 'clients'), orderBy('name', 'asc')));
        } else {
          snapshot = await getDocs(query(collection(db, 'clients'), where('userId', '==', auth.currentUser.uid)));
        }
        const clientList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (userRole !== 'admin') {
          clientList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        }
        setClients(clientList);
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };

    fetchClients();
  }, [userRole]);

  const handleClientSelect = (e) => {
    const id = e.target.value;
    if (!id) return;
    const client = clients.find(c => c.id === id);
    if (!client) return;
    setFormData({
      ...formData,
      clientName: client.name || '',
      clientEmail: client.email || '',
      clientPhone: client.phone || '',
      clientAddress: client.address || ''
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Update both naming conventions for customer/client fields
    if (name === 'clientName') {
      setFormData({
        ...formData,
        clientName: value,
        customerName: value
      });
    } else if (name === 'clientEmail') {
      setFormData({
        ...formData,
        clientEmail: value,
        customerEmail: value
      });
    } else if (name === 'clientAddress') {
      setFormData({
        ...formData,
        clientAddress: value,
        customerAddress: value
      });
    } else if (name === 'clientPhone') {
      setFormData({
        ...formData,
        clientPhone: value,
        customerPhone: value
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, price: 0 }]);
  };

  const removeItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const calculateTotals = () => calculateVat(items, formData.vatRate, formData.taxInclusive);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result);
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoClick = () => {
    fileInputRef.current.click();
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Get next invoice number from counter
      let invoiceNumber;
      await runTransaction(db, async (transaction) => {
        const counterRef = doc(db, 'counters', 'invoices');
        const counterDoc = await transaction.get(counterRef);
        
        let nextNumber = 1;
        if (counterDoc.exists()) {
          nextNumber = counterDoc.data().count + 1;
        }
        
        transaction.set(counterRef, { count: nextNumber });
        invoiceNumber = `INV-${nextNumber.toString().padStart(6, '0')}`;
      });
      
      // Calculate VAT-related values
      const totals = calculateTotals();
      
      const invoiceData = {
        ...formData,
        items,
        subtotal: totals.subtotal,
        vatAmount: totals.vatAmount,
        vatRate: Number(formData.vatRate),
        taxInclusive: formData.taxInclusive,
        taxRegNo: formData.taxRegNo,
        total: totals.total,
        status: 'pending',
        invoiceNumber,
        invoiceNo: invoiceNumber, // Add both naming conventions
        userId: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        dueDate: formData.dueDate ? new Date(formData.dueDate) : null,
        nextRunDate: formData.recurrenceFrequency !== 'none'
          ? advanceDate(formData.dueDate ? new Date(formData.dueDate) : new Date(), formData.recurrenceFrequency)
          : null,
        logoBase64: logo, // Store logo as base64 string directly in Firestore
        // Ensure both naming conventions are included
        customerName: formData.customerName || formData.clientName,
        customerEmail: formData.customerEmail || formData.clientEmail,
        customerAddress: formData.customerAddress || formData.clientAddress,
        customerPhone: formData.customerPhone || formData.clientPhone,
        clientName: formData.clientName || formData.customerName,
        clientEmail: formData.clientEmail || formData.customerEmail,
        clientAddress: formData.clientAddress || formData.customerAddress,
        clientPhone: formData.clientPhone || formData.customerPhone
      };
      
      await addDoc(collection(db, 'invoices'), invoiceData);
      navigate('/invoices');
    } catch (error) {
      console.error('Error creating invoice:', error);
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-section">
        <h1 className="form-title">Create New Invoice</h1>
        
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h2 className="form-section-title">Company Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Company Name</label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Your Company Name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Company Email</label>
                <input
                  type="email"
                  name="companyEmail"
                  value={formData.companyEmail}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="company@example.com"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Company Phone</label>
                <input
                  type="text"
                  name="companyPhone"
                  value={formData.companyPhone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="(123) 456-7890"
                />
              </div>
              <div className="form-group form-grid-full">
                <label className="form-label">Company Address</label>
                <textarea
                  name="companyAddress"
                  value={formData.companyAddress}
                  onChange={handleChange}
                  className="form-textarea"
                  rows="2"
                  placeholder="Company Address"
                ></textarea>
              </div>
              <div className="form-group form-grid-full">
                <label className="form-label">Company Logo</label>
                <div className="logo-upload-container">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleLogoChange}
                    accept="image/*"
                    className="hidden-file-input"
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={handleLogoClick}
                    className="logo-upload-btn"
                  >
                    {logoPreview ? 'Change Logo' : 'Upload Logo'}
                  </button>
                  {logoPreview && (
                    <div className="logo-preview">
                      <img 
                        src={logoPreview} 
                        alt="Company logo preview" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '150px', 
                          objectFit: 'contain' 
                        }} 
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">VAT</label>
                <select
                  name="vatRate"
                  value={formData.vatRate}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value={0}>Not VAT registered</option>
                  <option value={0.15}>VAT registered (15%)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">VAT Registration No.</label>
                <input
                  type="text"
                  name="taxRegNo"
                  value={formData.taxRegNo}
                  onChange={handleChange}
                  disabled={Number(formData.vatRate) === 0}
                  className="form-input"
                  placeholder="e.g. 4123456789"
                />
              </div>
              <div className="form-group form-grid-full">
                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    name="taxInclusive"
                    checked={formData.taxInclusive}
                    onChange={handleCheckboxChange}
                    disabled={Number(formData.vatRate) === 0}
                    className="form-checkbox-input"
                  />
                  <span>Prices already include VAT (tax-inclusive)</span>
                </label>
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h2 className="form-section-title">Client Information</h2>
            <div className="form-grid">
              {clients.length > 0 && (
                <div className="form-group form-grid-full">
                  <label className="form-label">Saved Client (auto-fill)</label>
                  <select
                    onChange={handleClientSelect}
                    defaultValue=""
                    className="form-select"
                  >
                    <option value="">Select a saved client…</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Client Name</label>
                <input
                  type="text"
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="Client Name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Client Email</label>
                <input
                  type="email"
                  name="clientEmail"
                  value={formData.clientEmail}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="client@example.com"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Client Phone</label>
                <input
                  type="text"
                  name="clientPhone"
                  value={formData.clientPhone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Client Phone"
                />
              </div>
              <div className="form-group form-grid-full">
                <label className="form-label">Client Address</label>
                <textarea
                  name="clientAddress"
                  value={formData.clientAddress}
                  onChange={handleChange}
                  className="form-textarea"
                  rows="2"
                  placeholder="Client Address"
                ></textarea>
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h2 className="form-section-title">Invoice Details</h2>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Due Date</label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Repeat Invoice</label>
                <select
                  name="recurrenceFrequency"
                  value={formData.recurrenceFrequency}
                  onChange={handleChange}
                  className="form-select"
                >
                  {RECURRENCE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <div className="items-header">
              <h2 className="form-section-title" style={{ marginBottom: 0 }}>Invoice Items</h2>
              <button
                type="button"
                onClick={addItem}
                className="add-item-btn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
                </svg>
                Add Item
              </button>
            </div>
            
            <div className="items-container">
              <div className="items-grid-header">
                <div>Description</div>
                <div className="text-center">Quantity</div>
                <div className="text-center">Price (ZAR)</div>
                <div className="text-right">Amount</div>
                <div></div>
              </div>
              
              {items.map((item, index) => (
                <div key={index} className="item-row">
                  <input
                    type="text"
                    placeholder="e.g. Website design service"
                    value={item.description}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    required
                    className="item-input item-input--description"
                  />
                  <input
                    type="number"
                    placeholder="1"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                    min="1"
                    required
                    className="item-input item-input--quantity"
                  />
                  <input
                    type="number"
                    placeholder="0.00"
                    value={item.price}
                    onChange={(e) => handleItemChange(index, 'price', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                    required
                    className="item-input item-input--price"
                  />
                  <span className="item-amount">
                    {formatCurrency((item.quantity || 0) * (item.price || 0))}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="item-remove-btn"
                    disabled={items.length === 1}
                    aria-label={`Remove item ${index + 1}`}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14ZM10 11v6M14 11v6" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            
            <div className="items-total">
              <div className="items-total-row">
                <span>Subtotal{Number(formData.vatRate) > 0 && !formData.taxInclusive ? ' (excl. VAT)' : ''}</span>
                <span>{formatCurrency(calculateTotals().subtotal)}</span>
              </div>
              
              {Number(formData.vatRate) > 0 && (
                <div className="items-total-row">
                  <span>VAT ({(Number(formData.vatRate) * 100).toFixed(0)}%{formData.taxInclusive ? ', incl.' : ''})</span>
                  <span>{formatCurrency(calculateTotals().vatAmount)}</span>
                </div>
              )}
              
              <div className="items-total-row items-total-row--grand">
                <span>{Number(formData.vatRate) > 0 && formData.taxInclusive ? 'Total (incl. VAT)' : 'Total'}</span>
                <span>{formatCurrency(calculateTotals().total)}</span>
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h2 className="form-section-title">Additional Notes</h2>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="form-textarea"
              rows="3"
              placeholder="Add any additional notes or payment instructions"
            ></textarea>
          </div>
          
          <div className="form-footer">
            <button
              type="submit"
              className="submit-btn"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
